import { Alert, AlertDescription } from '@/components/ui/alert';
import { trpc } from 'src/trpc';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useId, useState } from 'react';
import { safeMoneyFromCents } from '@libs/services/money';
import { useToast } from 'src/components/useToast';
import { OrderAddOn } from '@libs/models';
import { Trash2, Plus, ChevronUp } from 'lucide-react';

interface OrderAddOnsDialogProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
  addOns: OrderAddOn[];
}

export const OrderAddOnsDialog = ({ open, onClose, orderId, addOns }: OrderAddOnsDialogProps) => {
  const [error, setError] = useState<null | string>(null);
  const [showAddMenu, setShowAddMenu] = useState(false);
  // Ties the Add Item disclosure to the menu it opens. `useId` keeps it unique per mounted
  // sheet, so the association survives the sheet being opened and closed repeatedly.
  const addMenuId = useId();
  const utils = trpc.useUtils();
  const { successToast, errorToast } = useToast();

  // Fetch all available add-ons for the organization (including non-customer-visible)
  const { data: availableAddOns, isLoading: loadingAddOns } = trpc.admin.addOn.getAll.useQuery({
    offset: 0,
    limit: 100,
  });

  // Fetch all available products for the organization
  const { data: availableProducts, isLoading: loadingProducts } =
    trpc.admin.product.getAllProducts.useQuery({
      offset: 0,
      limit: 100,
    });

  const { mutateAsync, isPending: updateLoading } =
    trpc.admin.order.adminUpdateOrderAddOns.useMutation({
      onSuccess: () => {
        utils.admin.order.adminGetOrderRelatedInfoById.invalidate(orderId);
      },
      onError(err) {
        setError('Unable to update add-ons.');
        errorToast({ title: 'Error', description: err.message });
      },
    });

  const handleRemoveItem = async (index: number) => {
    const updatedAddOns = addOns.filter((_, i) => i !== index);
    await mutateAsync({
      orderId,
      addOns: updatedAddOns,
    });
    successToast({ description: 'Item removed successfully.' });
  };

  const handleAddItem = async (item: {
    _id: string;
    title: string;
    priceInCents: number;
    isProduct: boolean;
  }) => {
    const newAddOn: OrderAddOn = {
      _id: item._id,
      title: item.title,
      priceInCents: item.priceInCents,
      isProduct: item.isProduct,
    };

    const updatedAddOns = [...addOns, newAddOn];
    await mutateAsync({
      orderId,
      addOns: updatedAddOns,
    });

    setShowAddMenu(false);
    successToast({ description: `Added "${item.title}" to order.` });
  };

  const handleClose = () => {
    setShowAddMenu(false);
    setError(null);
    onClose();
  };

  // Filter out already-added add-ons and archived ones from the dropdown
  const availableToAdd =
    availableAddOns?.addOns?.filter(
      (a) =>
        !a.archived &&
        !addOns.some((existing) => existing._id === a._id?.toString() && !existing.isProduct),
    ) || [];

  // Filter out already-added products and archived ones from the dropdown
  const productsToAdd =
    availableProducts?.products?.filter(
      (p) =>
        !p.archived &&
        !addOns.some((existing) => existing._id === p._id?.toString() && existing.isProduct),
    ) || [];

  // Calculate total
  const totalPriceInCents = addOns.reduce((acc, addOn) => acc + addOn.priceInCents, 0);

  const isLoading = loadingAddOns || loadingProducts;
  const hasAvailableItems = availableToAdd.length > 0 || productsToAdd.length > 0;

  return (
    <Sheet
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          handleClose();
        }
      }}
    >
      <SheetContent className="sm:max-w-[500px] h-full p-0">
        <div className="grid h-full grid-rows-[auto_1fr_auto]">
          <SheetHeader className="border-b bg-white px-6 py-5">
            <SheetTitle className="text-lg font-semibold text-text-primary">
              Manage Add-ons & Products
            </SheetTitle>
            <SheetDescription className="text-sm text-text-light mt-0.5">
              Add or remove items from this order
            </SheetDescription>
          </SheetHeader>

          <div className="overflow-y-auto px-6 py-4">
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Current Items */}
            <div className="space-y-2">
              {addOns.map((item, index) => (
                <div
                  key={`${item._id}-${index}`}
                  className="flex items-center justify-between p-4 rounded-lg bg-secondary border border-disabled"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-text-primary">{item.title}</h3>
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          item.isProduct ? 'bg-info/10 text-info' : 'bg-disabled text-text-light'
                        }`}
                      >
                        {item.isProduct ? 'Product' : 'Add-on'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <p className="font-semibold text-text-primary">
                      {safeMoneyFromCents(item.priceInCents).toFormat()}
                    </p>
                    {/* Icon-only, so the item it removes has to be in its accessible name:
                        a list of identical "Remove item" buttons tells a screen-reader user
                        which control they are on but not which item it takes off the order. */}
                    <button
                      onClick={() => handleRemoveItem(index)}
                      className="p-1.5 text-destructive hover:bg-light-destructive rounded transition-colors"
                      aria-label={`Remove ${item.title}`}
                      title={`Remove ${item.title}`}
                      disabled={updateLoading}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}

              {addOns.length === 0 && (
                <div className="text-center py-8 text-text-disabled">
                  <p>No items added</p>
                </div>
              )}
            </div>

            {/* Add Item Dropdown */}
            <div className="relative mt-4">
              <button
                onClick={() => setShowAddMenu(!showAddMenu)}
                disabled={isLoading || !hasAvailableItems || updateLoading}
                aria-expanded={showAddMenu}
                aria-controls={addMenuId}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-disabled rounded-lg text-text-light hover:border-text-light hover:text-text-primary hover:bg-secondary transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-5 h-5" />
                <span className="font-medium">
                  {isLoading
                    ? 'Loading...'
                    : !hasAvailableItems
                      ? 'No items available'
                      : 'Add Item'}
                </span>
                <ChevronUp
                  className={`w-4 h-4 transition-transform ${showAddMenu ? '' : 'rotate-180'}`}
                />
              </button>

              {/* Dropdown Menu */}
              {showAddMenu && (
                <div
                  id={addMenuId}
                  className="absolute top-full left-0 right-0 mt-2 bg-white border border-disabled rounded-lg shadow-lg z-10 overflow-hidden max-h-64 overflow-y-auto"
                >
                  {/* Add-ons Section */}
                  {availableToAdd.length > 0 && (
                    <>
                      <div className="px-4 py-2 bg-secondary border-b border-disabled">
                        <p className="text-xs font-semibold text-text-light uppercase tracking-wide">
                          Add-ons
                        </p>
                      </div>
                      {availableToAdd.map((addOn) => (
                        <button
                          key={addOn._id?.toString()}
                          onClick={() =>
                            handleAddItem({
                              _id: addOn._id!.toString(),
                              title: addOn.title,
                              priceInCents: addOn.priceInCents,
                              isProduct: false,
                            })
                          }
                          disabled={updateLoading}
                          className="w-full flex items-center justify-between px-4 py-3 hover:bg-secondary transition-colors text-left border-b border-disabled last:border-0 disabled:opacity-50"
                        >
                          <p className="font-medium text-text-primary text-sm">{addOn.title}</p>
                          <p className="text-sm font-semibold text-text-light">
                            {safeMoneyFromCents(addOn.priceInCents).toFormat()}
                          </p>
                        </button>
                      ))}
                    </>
                  )}

                  {/* Products Section */}
                  {productsToAdd.length > 0 && (
                    <>
                      <div className="px-4 py-2 bg-secondary border-y border-disabled">
                        <p className="text-xs font-semibold text-text-light uppercase tracking-wide">
                          Products
                        </p>
                      </div>
                      {productsToAdd.map((product) => (
                        <button
                          key={product._id?.toString()}
                          onClick={() =>
                            handleAddItem({
                              _id: product._id!.toString(),
                              title: product.label,
                              priceInCents: product.priceInCents,
                              isProduct: true,
                            })
                          }
                          disabled={updateLoading}
                          className="w-full flex items-center justify-between px-4 py-3 hover:bg-secondary transition-colors text-left border-b border-disabled last:border-0 disabled:opacity-50"
                        >
                          <p className="font-medium text-text-primary text-sm">{product.label}</p>
                          <p className="text-sm font-semibold text-text-light">
                            {safeMoneyFromCents(product.priceInCents).toFormat()}
                          </p>
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer / Total */}
          <div className="px-6 py-4 bg-secondary border-t border-disabled">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-light">Total</p>
                <p className="text-2xl font-bold text-text-primary">
                  {safeMoneyFromCents(totalPriceInCents).toFormat()}
                </p>
              </div>
              <Button
                onClick={handleClose}
                variant="secondary"
                className="px-6 py-3 border border-gray-300 hover:bg-gray-100 text-gray-700 font-semibold rounded-lg transition-colors"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
